import { memo, lazy, Suspense } from "react";
import { Loader2, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import spacexLogo from "@/assets/logos/spacex.png";
import pgLogo from "@/assets/logos/pg.png";
import dupontLogo from "@/assets/logos/dupont.png";

// Lazy load heavy components that are below the fold
const ProductGrid = lazy(() => import("@/components/ProductGrid").then(m => ({ default: m.ProductGrid })));

// Loading fallback for lazy components
const ComponentLoader = () => (
  <div className="flex justify-center items-center py-8">
    <Loader2 className="h-6 w-6 animate-spin text-primary" />
  </div>
);

// Memoized trusted logos section - fixed height to prevent CLS
const TrustedLogos = memo(() => (
  <div className="py-8 min-h-[120px]">
    <p className="text-sm uppercase tracking-wider text-muted-foreground mb-6 font-semibold h-5">Trusted by Industry Leaders</p>
    <div className="flex flex-wrap items-center justify-center gap-12 md:gap-16 min-h-[48px]">
      <div className="flex items-center gap-3 group">
        <div className="w-12 h-12 rounded-lg overflow-hidden shadow-md group-hover:shadow-lg transition-shadow duration-200">
          <img src={spacexLogo} alt="SpaceX" className="w-full h-full object-contain" width="48" height="48" />
        </div>
        <span className="text-lg font-semibold text-foreground/80">SpaceX</span>
      </div>
      <div className="flex items-center gap-3 group">
        <div className="w-12 h-12 rounded-lg overflow-hidden shadow-md group-hover:shadow-lg transition-shadow duration-200 bg-white p-1">
          <img src={pgLogo} alt="Procter & Gamble" className="w-full h-full object-contain" width="48" height="48" />
        </div>
        <span className="text-lg font-semibold text-foreground/80">Procter & Gamble</span>
      </div>
      <div className="flex items-center gap-3 group">
        <div className="w-12 h-12 rounded-lg overflow-hidden shadow-md group-hover:shadow-lg transition-shadow duration-200 bg-white p-1">
          <img src={dupontLogo} alt="DuPont" className="w-full h-full object-contain" width="48" height="48" />
        </div>
        <span className="text-lg font-semibold text-foreground/80">DuPont</span>
      </div>
    </div>
  </div>
));

TrustedLogos.displayName = "TrustedLogos";

// Memoized industry tags
const IndustryTags = memo(() => (
  <div className="flex flex-wrap justify-center gap-3 pt-4">
    <span className="px-5 py-2.5 bg-primary/90 text-primary-foreground rounded-full text-sm font-semibold shadow-md hover:shadow-lg hover:bg-primary transition-all duration-300">Food & Beverage</span>
    <span className="px-5 py-2.5 bg-secondary/90 text-secondary-foreground rounded-full text-sm font-semibold shadow-md hover:shadow-lg hover:bg-secondary transition-all duration-300">Agriculture</span>
    <span className="px-5 py-2.5 bg-primary/90 text-primary-foreground rounded-full text-sm font-semibold shadow-md hover:shadow-lg hover:bg-primary transition-all duration-300">Manufacturing</span>
    <span className="px-5 py-2.5 bg-secondary/90 text-secondary-foreground rounded-full text-sm font-semibold shadow-md hover:shadow-lg hover:bg-secondary transition-all duration-300">System Builders</span>
    <span className="px-5 py-2.5 bg-primary/90 text-primary-foreground rounded-full text-sm font-semibold shadow-md hover:shadow-lg hover:bg-primary transition-all duration-300">Service & Maintenance</span>
  </div>
));

IndustryTags.displayName = "IndustryTags";

// Memoized authorized brands
const AuthorizedBrands = memo(() => (
  <div className="pt-12 mt-12 relative">
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent h-px top-0"></div>
    <p className="text-sm uppercase tracking-widest text-primary mb-6 font-bold">Authorized Distributor</p>
    <div className="flex flex-wrap justify-center gap-6 text-lg">
      <span className="px-6 py-3 bg-gradient-to-br from-card via-card to-muted/30 rounded-xl font-bold text-foreground hover:text-primary hover:shadow-lg hover:scale-105 transition-all duration-300 border border-border/50 backdrop-blur-sm">DuPont FilmTec</span>
      <span className="px-6 py-3 bg-gradient-to-br from-card via-card to-muted/30 rounded-xl font-bold text-foreground hover:text-primary hover:shadow-lg hover:scale-105 transition-all duration-300 border border-border/50 backdrop-blur-sm">Hydranautics</span>
      <span className="px-6 py-3 bg-gradient-to-br from-card via-card to-muted/30 rounded-xl font-bold text-foreground hover:text-primary hover:shadow-lg hover:scale-105 transition-all duration-300 border border-border/50 backdrop-blur-sm">Toray</span>
      <span className="px-6 py-3 bg-gradient-to-br from-card via-card to-muted/30 rounded-xl font-bold text-foreground hover:text-primary hover:shadow-lg hover:scale-105 transition-all duration-300 border border-border/50 backdrop-blur-sm">LG</span>
      <span className="px-6 py-3 bg-gradient-to-br from-card via-card to-muted/30 rounded-xl font-bold text-foreground hover:text-primary hover:shadow-lg hover:scale-105 transition-all duration-300 border border-border/50 backdrop-blur-sm">Protec</span>
    </div>
  </div>
));

AuthorizedBrands.displayName = "AuthorizedBrands";

export const Hero = memo(() => {
  return (
    <section className="relative py-12 px-4 bg-gradient-to-br from-background via-muted/20 to-background overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>
      </div>
      
      <div className="container mx-auto max-w-7xl">
        <div className="text-center space-y-4 mb-8">
          <div className="space-y-2">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
              Industrial RO Membranes <br />
              <span className="text-primary">& Pressure Vessels</span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Serving industry leaders for over <span className="text-primary font-semibold">12 years</span> with 
              great prices and quick lead times
            </p>
          </div>
        </div>

        {/* Browse Products Section - min-height reserves space to prevent layout shift */}
        <div className="min-h-[800px] lg:min-h-[900px]">
          <Suspense fallback={<ComponentLoader />}>
            <ProductGrid maxItems={12} />
          </Suspense>
        </div>
        
        {/* View All Products Link */}
        <div className="text-center mt-8">
          <Link to="/products">
            <Button size="lg" variant="outline" className="group">
              View All Products
              <ChevronRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>

        {/* Trusted section with fixed min-height to prevent CLS */}
        <div className="text-center space-y-8 mt-16 min-h-[420px]">
          <TrustedLogos />
          <IndustryTags />
          <AuthorizedBrands />
        </div>
      </div>
    </section>
  );
});

Hero.displayName = "Hero";
